import React, { useEffect, useState } from "react";
import { Search, ShieldCheck, Check, ChevronRight, Sparkles } from "lucide-react";
import { OwlLoader } from "@/components/OwlLoader";

const STEPS = [
  { key: "search", caption: "Search the market", ms: 1600 },
  { key: "screen", caption: "AAOIFI screening runs on live filings", ms: 2800 },
  { key: "ticket", caption: "Order ticket — every cost shown", ms: 2400 },
  { key: "filled", caption: "Filled, settled to your portfolio", ms: 2400 },
];

const RATIOS = [
  ["Debt ratio", "under 30%", "1.86%"],
  ["Non-permissible income", "under 5%", "0.00%"],
  ["Tangible assets", "over 20%", "76.4%"],
];

const Ratio = ({ label, limit, value, show }) => (
  <div className={`flex items-center gap-2 transition-all duration-500 ${
    show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1.5"}`}>
    <span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-emerald-500/12 shrink-0">
      <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={3.5} />
    </span>
    <span className="min-w-0 flex-1 truncate text-[10px] text-slate-500">
      {label} <span className="text-slate-400">· {limit}</span>
    </span>
    <span className="numeric text-[10px] font-semibold text-emerald-600">{value}</span>
  </div>
);

const Cost = ({ k, v, strong }) => (
  <div className="flex items-center justify-between">
    <span className={strong ? "text-[10.5px] font-semibold text-[#1A1330]" : "text-[10.5px] text-slate-500"}>{k}</span>
    <span className={`numeric text-[10.5px] ${strong ? "font-bold text-[#1A1330]" : "font-medium text-slate-700"}`}>{v}</span>
  </div>
);

/** A scripted walkthrough of a real trade being screened and placed — plays like a screen recording. */
export const TradeReel = ({ onCaption }) => {
  const [step, setStep] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setStep((s) => (s + 1) % STEPS.length); setTick(0); }, STEPS[step].ms);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    onCaption?.(STEPS[step].caption);
    const id = setInterval(() => setTick((n) => n + 1), 420);
    return () => clearInterval(id);
  }, [step, onCaption]);

  const screening = step === 1 || step === 2;
  const verdict = step > 1 || tick >= 2;

  return (
    <div className="px-3.5 pt-8" data-testid="phone-trade-reel">
      {/* search field */}
      <div className={`flex items-center gap-2 rounded-2xl bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(26,19,48,0.06)]
        ring-1 transition-all duration-300 ${step === 0 ? "ring-[color:var(--tharwy-lime)]/70" : "ring-black/5"}`}>
        <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="text-[12px] font-semibold tracking-tight text-[#1A1330]">
          AAPL{step === 0 && <span className="ms-0.5 font-normal text-[color:var(--tharwy-lime-700)] animate-pulse">|</span>}
        </span>
        <span className="ms-auto truncate text-[9.5px] text-slate-400">Apple Inc. · NASDAQ</span>
      </div>

      {/* shariah screening */}
      <div className={`mt-2 overflow-hidden rounded-2xl bg-white shadow-[0_2px_10px_-4px_rgba(26,19,48,0.18)] ring-1 ring-black/5
        transition-all duration-500 ${screening ? "max-h-[140px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-3 pt-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold tracking-tight text-[#1A1330]">Shariah screening</span>
            {verdict ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-[3px] text-[9px] font-bold uppercase tracking-wide text-emerald-700">
                <ShieldCheck className="h-2.5 w-2.5" />Compliant
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] text-slate-400">
                <OwlLoader size="xs" />reading filings
              </span>
            )}
          </div>
          <div className="mt-2 space-y-1.5 pb-2.5">
            {RATIOS.map(([label, limit, value], idx) => (
              <Ratio key={label} label={label} limit={limit} value={value} show={step > 1 || tick > idx} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 bg-[#F7F9EF] px-3 py-1.5 text-[8.5px] text-[#5C7020]">
          <Sparkles className="h-2.5 w-2.5" />Screened on filings dated 28 Jun · purification 0.00%
        </div>
      </div>

      {/* order ticket */}
      <div className={`mt-2 overflow-hidden rounded-2xl bg-white shadow-[0_2px_10px_-4px_rgba(26,19,48,0.18)] ring-1 ring-black/5
        transition-all duration-500 ${step >= 2 ? "max-h-[160px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="p-3">
          <div className="flex items-baseline justify-between">
            <span className="text-[11px] font-semibold tracking-tight text-[#1A1330]">Buy AAPL · market</span>
            <span className="numeric text-[12px] font-bold text-[#1A1330]">$228.65</span>
          </div>
          <div className="mt-2 space-y-1">
            {step === 3 ? (
              <>
                <Cost k="12 shares at $228.61" v="$2,743.32" />
                <Cost k="Total paid" v="$2,743.32" strong />
              </>
            ) : (
              <>
                <Cost k="Quantity" v="12 shares" />
                <Cost k="Commission" v="$0.00" />
                <Cost k="FX from your wallet" v="0.30%" />
                <Cost k="Total to pay" v="$2,743.80" strong />
              </>
            )}
          </div>
          {step === 3 ? (
            <div className="mt-2.5 flex items-center justify-center gap-1.5 rounded-full bg-[#EFF6D8] py-2 text-[10.5px] font-bold text-[#3E2E65]">
              <Check className="h-3 w-3" strokeWidth={3} />Order placed
            </div>
          ) : (
            <div className="relative mt-2.5 h-8 overflow-hidden rounded-full bg-[#332654]">
              <span className="absolute inset-0 grid place-items-center text-[10px] font-semibold text-white/80">
                Swipe to confirm
              </span>
              <span className="swipe-thumb absolute top-1 start-1 grid h-6 w-6 place-items-center rounded-full bg-[color:var(--tharwy-lime)]">
                <ChevronRight className="h-3.5 w-3.5 text-[#332654]" strokeWidth={3} />
              </span>
            </div>
          )}
        </div>
      </div>

      {/* fill confirmation */}
      {step === 3 && (
        <div className="mt-2 flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#332654] to-[#4A3775] p-3 text-white shadow-lg">
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[color:var(--tharwy-lime)]">
            <Check className="h-4 w-4 text-[#332654]" strokeWidth={3} />
          </span>
          <span className="min-w-0">
            <span className="block text-[11px] font-semibold">Filled at $228.61</span>
            <span className="block text-[9px] text-white/60">12 shares · settles T+1 · halal ✓</span>
          </span>
        </div>
      )}

      {/* step rail */}
      <div className="mt-3 flex items-center gap-1">
        {STEPS.map((s, idx) => (
          <span key={s.key} className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${
            idx <= step ? "bg-[color:var(--tharwy-lime)]" : "bg-black/10"}`} />
        ))}
      </div>
    </div>
  );
};

export const TRADE_REEL_MS = STEPS.reduce((a, s) => a + s.ms, 0);
